/**
 * The buses about the player, fetched and laid out for the traffic.
 *
 * In Västtrafik's area the Worker answers with the real ones
 * (`/rt/transit/vasttrafik`, see realtime/transit.ts): fetched every half
 * minute, and again on going a good way, each trip's best known times
 * replacing the last. Trips gathered before stay until their last stop is
 * behind them: the Worker looks at the stops about the player, and a bus
 * that has driven on from them is still out on its way.
 *
 * Elsewhere, and without the Worker's credentials, the buses are
 * OpenStreetMap's: its bus routes within 2 km, every five minutes or on going
 * a kilometre and more, run to a made-up timetable. In Sweden they are read
 * from our own route tiles beside the map's (tools/osmsweden/routes.py), with
 * each route's stops in order; elsewhere they are asked of Overpass
 * (`core/transit/osmroutes.ts`). In Västtrafik's area they wear its designs
 * by their line numbers; elsewhere the plain bus, in the route's colour.
 *
 * Each trip is laid out in scene x, z (east, -north) for the traffic
 * (`Traffic.setTransit`), and laid out again when the origin moves under it,
 * so the buses stay on their ways through a rebase.
 */

import type { Traffic } from '../three/traffic.js';
import type { TripLayout } from '../core/transit/trip.js';
import type { TransitTrip } from '../core/transit/trip.js';
import { layoutTrip } from '../core/transit/trip.js';
import { lookOf, readTrips } from '../core/transit/vasttrafik.js';
import type { OsmRoute } from '../core/transit/osmroutes.js';
import { overpassRoutesQuery, parseRoutes, readRouteTile, scheduleRoute } from '../core/transit/osmroutes.js';
import { OSM_ROUTES_BASE } from '../config.js';

/** Sweden, roughly: where the route tiles are, and Overpass is not asked. */
const SWEDEN = { south: 55.3, north: 69.1, west: 10.9, east: 24.2 };
const ROUTE_ZOOM = 12;

const OVERPASS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
];
/** OpenStreetMap's routes: how far about the player, metres; how often, ms; how far moved before again, metres. */
const ROUTES_REACH = 2000;
const ROUTES_MS = 5 * 60_000;
const ROUTES_MOVED = 1100;

/** Where Västtrafik runs, roughly: Västra Götaland and round it. */
const VASTTRAFIK = { south: 57.1, north: 59.5, west: 10.9, east: 14.8 };
/** How often the buses about are asked for, ms, and how far one goes before they are asked for again, metres. */
const POLL_MS = 30_000;
const MOVED = 700;

export class TransitFeed {
  private trips = new Map<string, TransitTrip>();
  private fetching = false;
  private last = -Infinity;
  private lastLat = NaN;
  private lastLon = NaN;
  /** The Worker has no credentials: not asked again this session. */
  private unavailable = false;
  /** Where the first stop of the first trip was in the scene when laid out, to see the origin move. */
  private anchor: { lat: number; lon: number; east: number; north: number } | undefined;
  private dirty = false;
  /** OpenStreetMap's routes about, when they are the buses: fetched, and where and when from. */
  private routes: OsmRoute[] = [];
  private routesAt = -Infinity;
  private routesLat = NaN;
  private routesLon = NaN;
  private routesFetching = false;
  private scheduledAt = -Infinity;

  constructor(private readonly url = '/rt/transit/vasttrafik') {}

  /**
   * Each frame: ask when due, and lay out again when there is something new
   * or the origin has moved. `hour` is the local hour, for how often the
   * made-up timetables run.
   */
  update(now: number, hour: number, lat: number, lon: number, toLocal: (lat: number, lon: number) => { east: number; north: number }, traffic: Traffic,
    trams?: { setTrips(trips: Array<{ trip: TransitTrip; layout: TripLayout }>): void }): void {
    const inside = lat > VASTTRAFIK.south && lat < VASTTRAFIK.north && lon > VASTTRAFIK.west && lon < VASTTRAFIK.east;
    if (!inside || this.unavailable) this.fromMap(now, hour, lat, lon, inside);
    const moved = Number.isFinite(this.lastLat)
      ? Math.hypot((lat - this.lastLat) * 111_320, (lon - this.lastLon) * 111_320 * Math.cos(lat * Math.PI / 180)) : Infinity;
    if (inside && !this.unavailable && !this.fetching && (now - this.last > POLL_MS || moved > MOVED)) {
      this.last = now;
      this.lastLat = lat;
      this.lastLon = lon;
      void this.fetch(lat, lon);
    }
    if (this.anchor !== undefined) {
      const at = toLocal(this.anchor.lat, this.anchor.lon);
      if (Math.abs(at.east - this.anchor.east) > 0.01 || Math.abs(at.north - this.anchor.north) > 0.01) this.dirty = true;
    }
    if (!this.dirty) return;
    this.dirty = false;
    // Gone once their last stop is a minute behind them.
    for (const [id, trip] of this.trips) if (trip.stops[trip.stops.length - 1].arrive < now - 60_000) this.trips.delete(id);
    const project = (la: number, lo: number): [number, number] => {
      const p = toLocal(la, lo);
      return [p.east, -p.north];
    };
    const laid = [...this.trips.values()].map(trip => ({ trip, layout: layoutTrip(trip, project) }));
    const first = laid[0]?.trip.stops[0];
    if (first !== undefined) {
      const at = toLocal(first.lat, first.lon);
      this.anchor = { lat: first.lat, lon: first.lon, east: at.east, north: at.north };
    }
    traffic.setTransit(laid.filter(l => l.trip.line.mode === 'bus'));
    trams?.setTrips(laid.filter(l => l.trip.line.mode === 'tram'));
  }

  /** OpenStreetMap's routes for the buses: fetched when due, their timetables made up every half minute. */
  private fromMap(now: number, hour: number, lat: number, lon: number, vasttrafik: boolean): void {
    const moved = Number.isFinite(this.routesLat)
      ? Math.hypot((lat - this.routesLat) * 111_320, (lon - this.routesLon) * 111_320 * Math.cos(lat * Math.PI / 180)) : Infinity;
    if (!this.routesFetching && (now - this.routesAt > ROUTES_MS || moved > ROUTES_MOVED)) {
      this.routesAt = now;
      this.routesLat = lat;
      this.routesLon = lon;
      void this.fetchRoutes(lat, lon);
    }
    if (now - this.scheduledAt < POLL_MS || this.routes.length === 0) return;
    this.scheduledAt = now;
    const fresh = new Map<string, TransitTrip>();
    for (const route of this.routes) {
      for (const trip of scheduleRoute(route, now, hour, vasttrafik ? lookOf(route.ref) : 'plain')) fresh.set(trip.id, trip);
    }
    for (const [id, trip] of fresh) this.trips.set(id, trip);
    this.dirty = true;
  }

  private async fetchRoutes(lat: number, lon: number): Promise<void> {
    this.routesFetching = true;
    const dLat = ROUTES_REACH / 110_540, dLon = ROUTES_REACH / (111_320 * Math.cos(lat * Math.PI / 180));
    if (lat > SWEDEN.south && lat < SWEDEN.north && lon > SWEDEN.west && lon < SWEDEN.east) {
      try {
        this.routes = await this.routeTiles(lat, lon, dLat, dLon);
        this.scheduledAt = -Infinity;
      } finally {
        this.routesFetching = false;
      }
      return;
    }
    const query = overpassRoutesQuery(lat - dLat, lon - dLon, lat + dLat, lon + dLon);
    try {
      for (const server of OVERPASS) {
        try {
          const response = await fetch(server, { method: 'POST', body: `data=${encodeURIComponent(query)}`, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
          if (!response.ok) continue;
          this.routes = parseRoutes(await response.json());
          this.scheduledAt = -Infinity;
          return;
        } catch {
          // The next server.
        }
      }
    } finally {
      this.routesFetching = false;
    }
  }

  /**
   * The routes in Sweden's route tiles about (lat, lon): each route once, cut
   * as the tile the player is in has it, else as the one with most of it.
   */
  private async routeTiles(lat: number, lon: number, dLat: number, dLon: number): Promise<OsmRoute[]> {
    const n = 2 ** ROUTE_ZOOM;
    const tx = (lo: number) => Math.floor((lo + 180) / 360 * n);
    const ty = (la: number) => Math.floor((1 - Math.asinh(Math.tan(la * Math.PI / 180)) / Math.PI) / 2 * n);
    const here = `${tx(lon)}/${ty(lat)}`;
    const keys = new Set<string>();
    for (const la of [lat - dLat, lat, lat + dLat]) for (const lo of [lon - dLon, lon, lon + dLon]) keys.add(`${tx(lo)}/${ty(la)}`);
    const best = new Map<number, { route: OsmRoute; here: boolean }>();
    await Promise.all([...keys].map(async key => {
      try {
        const response = await fetch(`${OSM_ROUTES_BASE}/${ROUTE_ZOOM}/${key}.json`);
        if (!response.ok) return;
        for (const route of readRouteTile(await response.json())) {
          const had = best.get(route.id), mine = key === here;
          if (had === undefined || (mine && !had.here) || (mine === had.here && route.path.length > had.route.path.length)) best.set(route.id, { route, here: mine });
        }
      } catch {
        // No tile there, or offline: none from it.
      }
    }));
    return [...best.values()].map(b => b.route);
  }

  private async fetch(lat: number, lon: number): Promise<void> {
    this.fetching = true;
    try {
      const response = await fetch(`${this.url}?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`, { credentials: 'omit' });
      // A page with no Worker in front of it answers with itself, in HTML.
      const json = (response.headers.get('Content-Type') ?? '').includes('json');
      if (response.status === 503 || response.status === 404 || (response.ok && !json)) {
        // No Västtrafik credentials on the Worker, or no Worker: not asked again.
        this.unavailable = true;
        console.info('Buses: no live Västtrafik data (the Worker has no VASTTRAFIK_KEY and VASTTRAFIK_SECRET).');
        return;
      }
      if (!response.ok) return;
      for (const trip of readTrips(await response.json())) {
        if (trip.line.mode !== 'bus' && trip.line.mode !== 'tram') continue;
        this.trips.set(trip.id, trip);
      }
      this.dirty = true;
    } catch {
      // Offline, or the Worker down: tried again in half a minute.
    } finally {
      this.fetching = false;
    }
  }
}
